import React from 'react';
import { ChevronDown } from 'lucide-react';
import { forms } from '../../styles/design-tokens';

/**
 * UnifiedSelect Component
 * Standard select/dropdown component with consistent styling
 * 
 * @param {string} label - Select label text
 * @param {string} error - Error message
 * @param {string} helper - Helper text
 * @param {boolean} required - Required field indicator
 * @param {boolean} disabled - Disabled state
 * @param {string} className - Additional CSS classes
 * @param {string} placeholder - Placeholder text for empty select
 * @param {Array} options - Array of option objects with {value, label} structure
 */
const UnifiedSelect = React.forwardRef(({ 
  label = '',
  error = '',
  helper = '',
  required = false,
  disabled = false,
  className = '',
  placeholder = 'Select an option',
  options = [],
  id = '',
  ...props 
}, ref) => {
  // Generate unique ID if not provided
  const selectId = id || `select-${Math.random()?.toString(36)?.substr(2, 9)}`;
  
  // Determine select class based on state
  const selectClass = error 
    ? `${forms?.input?.base} ${forms?.input?.error}` 
    : disabled 
    ? `${forms?.input?.base} ${forms?.input?.disabled}`
    : forms?.input?.base;
  
  return (
    <div className={`w-full ${className}`?.trim()}>
      {label && (
        <label htmlFor={selectId} className={forms?.label}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          disabled={disabled}
          className={`${selectClass} appearance-none pr-10 cursor-pointer`}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options?.map((option, index) => (
            <option key={index} value={option?.value}>
              {option?.label}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
      {error && (
        <p className={forms?.error}>{error}</p>
      )}
      {helper && !error && (
        <p className={forms?.helper}>{helper}</p>
      )}
    </div>
  );
});

UnifiedSelect.displayName = 'UnifiedSelect';

export default UnifiedSelect;